import { useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import type { VoidFunctionComponent } from 'react';

import { Icon, icons } from '../../Icon';
import Tooltip from '../../Tooltip';

import styles from './actions.module.less';

type TooltipText = 'Copy link' | 'Link copied';

type Props = {
  url: string;
  tooltipId?: string;
};

/**
 * Generic component that renders a link icon button for copying URLs to clipboard.
 *
 * This component provides a consistent share/copy functionality across different
 * drawer types. It displays a link icon that copies the provided URL to the clipboard
 * when clicked, with visual feedback through tooltip state changes.
 *
 * @param props - Component props
 * @param props.url - The URL to copy to clipboard when the button is clicked
 * @param props.tooltipId - Optional custom tooltip ID for accessibility (defaults to 'share-link')
 *
 * @example
 * ```typescript
 * // Basic usage
 * <ShareLink url="https://example.com/snippet/123" />
 *
 * // With custom tooltip ID
 * <ShareLink url={shareUrl} tooltipId="share-snippet-link" />
 * ```
 */
const ShareLink: VoidFunctionComponent<Props> = ({ url, tooltipId = 'share-link' }) => {
  const [tooltip, setTooltip] = useState<TooltipText>('Copy link');
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Tooltip
      id={tooltipId}
      tooltip={tooltip}
      placement="bottom"
      onExited={() => {
        setTooltip('Copy link');
        if (buttonRef.current) {
          buttonRef.current.blur();
        }
      }}>
      <CopyToClipboard text={url}>
        <button
          ref={buttonRef}
          type="button"
          className={styles.shareLinkButton}
          onClick={() => {
            setTooltip('Link copied');
          }}
        >
          <Icon icon={icons.link} />
        </button>
      </CopyToClipboard>
    </Tooltip>
  );
};

export default ShareLink;
